// The Parts tab: the drawing's layers (top first), each layer's items as a tree
// (rooms and their features, groups, imported underlays), their rows' menus,
// renames and drags. docs/claude/layers.md.
import { unreachable } from '../../shared/exhaustive';
import { traitsOf } from '../../shared/categories';
import {
  defaultName,
  productName,
} from '../../shared/names';
import {
  type CeilingPlanView,
  type DrawingLayer,
  type FloorKind,
  type ProductInstance,
  type SaunaDoor,
  type Vec2,
} from '../../shared/types';
import {
  backrestColor,
  backrestsOf,
} from '../backrest';
import { productParts } from './catalogSync';
import { connDiscipline } from '../connections';
import {
  enterEdit,
  flashToolStatus,
  instLocked,
  invalidate,
  layers,
  leaveEdit,
  newId,
  pushHistory,
  redrawBackdrop,
  renderProperties,
  state,
} from './core';
import { showConfirm } from '../dialog';
import { roomDoors } from '../door';
import { arcSamplePts } from '../drawingObjects';
import { floorLabel } from '../flooring';
import {
  instanceToWorld,
  isFiniteBounds,
} from '../geometry';
import { glassPolysLocal } from '../glass';
import {
  groupById,
  groupMembers,
} from '../groups';
import {
  getUiLang,
  tr,
  trIn,
} from '../i18n';
import {
  addLayer,
  layerObjectIds,
  type LayerUnit,
  moveLayer,
  moveToLayer,
  type Placement,
  removeLayer,
  renameLayer,
  restack,
  setLayerFlag,
  unitObjectIds,
} from '../layers';
import {
  channelsOf,
  coveOf,
  LIGHT_AMBER,
  skiesOf,
  spotsOf,
  stripsOf,
} from '../lights';
import {
  numberedNames,
  objectName,
  polygonWord,
  wallWord,
} from '../naming';
import {
  heaterWallBands,
  heaterWallsOf,
  isPolyRoom,
  polyInnerWallBand,
  polyOpeningBand,
} from '../polyRoom';
import {
  sculptColor,
  sculptedBenchesOf,
} from '../sculptBench';
import {
  ANNOTATION_COLOR,
  CONNECTION_COLORS,
  defaultWallColor,
  pdfBoxCorners,
} from '../render';
import {
  connectionSchedule,
  scheduleNumbers,
} from '../schedule';
import { sectionLabel } from '../section';
import {
  clearSelection,
  dropFeature,
  featureOf,
  selectFeature,
  selectionOfIds,
  selectObjects,
  selectOne,
} from '../selection';
import { textBoxCorners } from '../textBox';
import {
  $,
  bgGeomBounds,
  bgImages,
  canEditStandards,
  connectionPath,
  deselectAll,
  dropLayerCache,
  editRoom,
  glassPanesOf,
  groupMembersSel,
  hideCtxMenu,
  importedLayer,
  innerWallsOf,
  LAYER_ENV,
  layerById,
  layerLabel,
  layerOfObject,
  objectOutline,
  openingsOf,
  pickDoor,
  pickSegments,
  rcpViewOutline,
  regenerateSauna,
  removeInnerWall,
  selDoorId,
  selectedObjectIds,
  selGlassId,
  selOpeningId,
  setObjectSelection,
  setTool,
  undoSteps,
  zoomToInstance,
  zoomToPoints,
} from '../main';
import {
  deleteObjectsById,
  deleteSelected,
  removeObjectsById,
} from './deleteSelection';
import {
  fixtureOutline,
  removeFixture,
} from './roomFixtures';
import { removeHeaterWall } from './heaterWalls';
import { buttonRow } from './panelRows';
import { openProductModalForInstance } from './productEditor';
import {
  SCHED_TABLES,
  schedKindOf,
  setPartName,
} from './scheduleTables';
import {
  removeSupport,
  supportMarksOf,
  supportWorld,
} from './benchSupportTool';
import {
  doorFootprint,
  roomDoor,
} from '../roomDoor';

/** This module's state that main.ts reads and writes too. */
export const partsTab = {
  /** The layer new objects land on while one is chosen (else their kind's own).
   *  View state, like AutoCAD's current layer between two sessions. */
  currentLayerId: null as string | null,
};

// Object highlighted (light-blue hatch) while its row is hovered in the Parts tab.
// Holds an instance id, an underlay image id, or the sentinel '__dxf__'.
export let hoverPartId: string | null = null;
// Group whose bounding box is highlighted while its Parts-tab group row is hovered.
export let hoverGroupId: string | null = null;
// Hidden heater wall highlighted while its Parts-tab row is hovered.
export let hoverHeaterWall: { instId: string; wallId: string } | null = null;
export let hoverInnerWall: { instId: string; wallId: string } | null = null;
// A Parts-tab row for a room FEATURE (door / glass pane / opening) highlights that
// feature, not its host room. The row supplies a thunk rather than an id because a
// feature has no id of its own; it is still called every frame, so the highlight
// tracks the geometry instead of freezing at mouseenter.
export let hoverFeatureRing: { key: string; ring: () => Vec2[] } | null = null;

/** Right-click menu for a Parts-tab instance row: zoom to it (animated) or rename
 *  it inline. `nameEl` is the row's name span, turned into an input on Rename. */
/** Remove one placed instance (and its room's auto fixtures / grouped members'
 *  side effects) from the drawing, undoably. Mirrors the instance branch of
 *  deleteSelected for a single target. */
function deletePartInstance(m: ProductInstance): void {
  if (!state.drawing.instances.some((i) => i.id === m.id)) return;
  pushHistory();
  removeObjectsById(new Set([m.id]));
  dropGoneInstancesFromSelection();
  renderProperties();
  invalidate();
}

/** The selected parts that a delete took (a room's fixtures with it) let go. */
export function dropGoneInstancesFromSelection(): void {
  const live = new Set(state.drawing.instances.map((i) => i.id));
  if (state.selectedId && !live.has(state.selectedId)) state.selectedId = null;
  state.selectedIds = state.selectedIds.filter((id) => live.has(id));
}

/** A parts-tab row context menu for any object that isn't a product instance
 *  (walls, shapes, imported underlays, …): Zoom to / Rename… / Delete, matching
 *  the instance-row menu. Callbacks are bound to the specific object; `del`
 *  typically selects then routes through deleteSelected so each type's own
 *  removal + undo runs. Rename is omitted for objects without an editable name. */
export function showObjectRowMenu(
  e: MouseEvent,
  opts: { zoomTo?: () => void; rename?: () => void; del?: () => void; lockId?: string },
): void {
  // A row on a hidden or locked layer still zooms, but edits nothing.
  const guard = (fn?: () => void) => (fn && opts.lockId ? () => { if (!refuseLockedRow(opts.lockId!)) fn(); } : fn);
  opts = { ...opts, rename: guard(opts.rename), del: guard(opts.del) };
  const menu = $('ctx-menu');
  menu.innerHTML = '';
  const item = (label: string, fn: () => void, danger = false): void => {
    const b = document.createElement('button');
    b.textContent = '   ' + label;
    if (danger) b.className = 'danger';
    b.addEventListener('click', () => { hideCtxMenu(); fn(); });
    menu.appendChild(b);
  };
  if (opts.zoomTo) item(tr('Zoom to'), opts.zoomTo);
  if (opts.rename) item(tr('Rename…'), opts.rename);
  if (opts.del) item(tr('Delete'), opts.del, true);
  menu.classList.remove('hidden');
  const h = menu.offsetHeight;
  menu.style.left = Math.min(e.clientX, window.innerWidth - 190) + 'px';
  menu.style.top = Math.max(4, Math.min(e.clientY, window.innerHeight - h - 4)) + 'px';
}

function showPartRowMenu(e: MouseEvent, m: ProductInstance, nameEl: HTMLElement): void {
  const menu = $('ctx-menu');
  menu.innerHTML = '';
  const item = (label: string, fn: () => void, danger = false): void => {
    const b = document.createElement('button');
    b.textContent = '   ' + label;
    if (danger) b.className = 'danger';
    b.addEventListener('click', () => { hideCtxMenu(); fn(); });
    menu.appendChild(b);
  };
  // A part on a hidden or locked layer still zooms and shows its properties, but
  // edits nothing.
  const guard = (fn: () => void) => (): void => { if (!refuseLockedRow(m.id)) fn(); };
  item(tr('Zoom to'), () => zoomToInstance(m));
  item(tr('Rename…'), guard(() => startPartRename(m, nameEl)));
  // Same wording rule as the catalogue card's menu: without the `edit` claim the
  // window opens to look at, not to change.
  const open = (): void => openProductModalForInstance(m);
  item(tr(canEditStandards() ? 'Edit properties…' : 'View properties…'), canEditStandards() ? guard(open) : open);
  item(tr('Delete'), guard(() => deletePartInstance(m)), true);
  menu.classList.remove('hidden');
  const h = menu.offsetHeight;
  menu.style.left = Math.min(e.clientX, window.innerWidth - 190) + 'px';
  menu.style.top = Math.max(4, Math.min(e.clientY, window.innerHeight - h - 4)) + 'px';
}

/** Turn a part row's name label into an inline text field; commit on Enter/blur,
 *  cancel on Escape. Writes the plain `name` (clearing any i18n map so the edit
 *  shows in every language). */
function startPartRename(m: ProductInstance, nameEl: HTMLElement): void {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'part-rename-input';
  input.value = m.enteredName ?? '';
  input.placeholder = defaultName(m.nameI18n, getUiLang(), m.name);
  nameEl.replaceWith(input);
  input.focus();
  input.select();
  let done = false;
  const commit = (save: boolean): void => {
    if (done) return; done = true;
    const v = input.value.trim();
    if (save && v !== (m.enteredName ?? '')) {
      pushHistory();
      setPartName(m.id, v);
      invalidate();
    }
    input.blur(); // the panel holds its rebuild while one of its fields has focus
    renderProperties(); // rebuild the list (restores the span, applies the new name)
  };
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); commit(true); }
    else if (ev.key === 'Escape') { ev.preventDefault(); commit(false); }
  });
  input.addEventListener('blur', () => commit(true));
  input.addEventListener('click', (ev) => ev.stopPropagation()); // don't select the row while editing
}

/** Right-click menu for a Parts-tab group row — Zoom to / Rename… / Delete,
 *  acting on the whole group (matching the single-part row menu). Rename writes
 *  the group's own name. */
function showGroupRowMenu(e: MouseEvent, gid: string, label: string, nameEl: HTMLElement): void {
  showObjectRowMenu(e, {
    zoomTo: () => zoomToPoints(groupOutlinePts(gid)),
    rename: () => startRowRename(nameEl, groupById(state.drawing, gid)?.name ?? '', label, (v) => { const g = groupById(state.drawing, gid); if (g) { if (v) g.name = v; else delete g.name; } }),
    del: () => { selectObjectFromList(() => setObjectSelection(groupMembersSel(gid))); deleteSelected(); },
    lockId: gid,
  });
}

/** Every outline point of a group's members, of every kind. */
export function groupOutlinePts(gid: string): Vec2[] {
  return groupMembers(state.drawing, gid).flatMap((m) => objectOutline(m.id) ?? []);
}

/** Inline-rename any parts-tab row: turn `nameEl` into a text field seeded with
 *  `current`; on commit (Enter/blur) run `apply(newName)` inside pushHistory. Used
 *  for walls, shapes and imported underlays (their plain `name` field). */
/** `isNew`: the thing was created just now, in its own undo step — naming it
 *  belongs to that step, not to a second one. */
function startRowRename(nameEl: HTMLElement, entered: string, shown: string, apply: (v: string) => void, isNew = false): void {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'part-rename-input';
  // The name entered, as typed; its default the placeholder (docs/claude/names.md).
  input.value = entered;
  input.placeholder = shown;
  nameEl.replaceWith(input);
  input.focus();
  input.select();
  let done = false;
  const commit = (save: boolean): void => {
    if (done) return; done = true;
    const v = input.value.trim();
    if (save && v !== entered) {
      if (!isNew) pushHistory();
      apply(v);
      // The name joins the creation's step; a gesture already open (this
      // field's own focus, or the press that ended the naming) begins after it.
      if (isNew && undoSteps.isOpen()) undoSteps.begin();
      invalidate();
    }
    input.blur(); // the panel holds its rebuild while one of its fields has focus
    renderProperties();
  };
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); commit(true); }
    else if (ev.key === 'Escape') { ev.preventDefault(); commit(false); }
  });
  input.addEventListener('blur', () => commit(true));
  input.addEventListener('click', (ev) => ev.stopPropagation());
}

function selectPartFromList(id: string): void {
  selectObjects(state, { instance: [id] }); // alone: no other object, no feature
  state.propTab = 'parts';
  renderProperties();
  invalidate();
}

/** Select a user-drawn polygon from the Parts list (mirrors selectPartFromList). */
function selectPolygonFromList(id: string): void {
  selectObjects(state, { polygon: [id] });
  leaveEdit('plan', 'underlay');
  state.propTab = 'parts';
  renderProperties();
  invalidate();
}

/** Select a user-drawn wall / line from the Parts list. */
function selectWallFromList(id: string): void {
  selectObjects(state, { wall: [id] });
  leaveEdit('plan', 'underlay');
  state.propTab = 'parts';
  renderProperties();
  invalidate();
}

/** One plain Parts-tab object row: coloured dot + label, click to select, the
 *  standard right-click menu (Zoom to / Rename where supported / Delete), and
 *  the hover highlight via `hoverPartId`. Used by the Constraints tab. */
export function partObjectRow(body: HTMLElement, opts: {
  color: string; label: string; active: boolean; hoverId: string;
  onClick: () => void; menu: (ev: MouseEvent, nameEl: HTMLElement) => void;
}): void {
  const r = document.createElement('div');
  r.className = 'part-row' + (opts.active ? ' active' : '');
  const dot = document.createElement('span');
  dot.className = 'disc-dot';
  dot.style.background = opts.color;
  const name = document.createElement('span');
  name.className = 'part-name';
  name.textContent = opts.label;
  r.append(dot, name);
  r.addEventListener('click', opts.onClick);
  r.addEventListener('contextmenu', (ev) => { ev.preventDefault(); ev.stopPropagation(); opts.menu(ev, name); });
  r.addEventListener('mouseenter', () => { hoverPartId = opts.hoverId; invalidate(); });
  r.addEventListener('mouseleave', () => { if (hoverPartId === opts.hoverId) { hoverPartId = null; invalidate(); } });
  body.appendChild(r);
}

/** Select one object from a Parts-tab row: the standard full deselect, then the
 *  caller sets its own id. Selection only — never pushes history. */
export function selectObjectFromList(apply: () => void): void {
  setTool('select');
  deselectAll();
  apply();
  renderProperties();
  invalidate();
}

/** Dot colour per floor kind in the Parts list — the plan hatch's stroke colour. */
const FLOOR_DOT: Record<FloorKind, string> = {
  'tiled-flat': '#e05a99', 'tiled-slope': '#8b5cf6', 'screed-flat': '#5aa9e6', 'screed-slope': '#2e7d5b',
};

// ---- Parts tab: the drawing's LAYERS (renderer/layers.ts) ----
// The tab lists the layers TOP FIRST (the one painted last heads the list), and
// inside each its items top of the stacking first — the reverse of the
// bottom-first `units`. A layer's header folds it, shows / hides it, locks it,
// renames it (double-click) and drags to restack the layers; an item's row drags
// to restack it among its siblings or onto another layer.

/** Layers folded in the Parts tab (view state, never in the drawing). */
const layersCollapsed = new Set<string>();
/** Collapsed tree-node keys inside a layer (default: everything expanded). */
const partsCollapsed = new Set<string>();

/** What a Parts-tab drag carries: a layer, an item (with where it sits), or an
 *  underlay of the Imported layer (they restack among themselves only). */
type LayerDrag =
  | { kind: 'layer'; id: string }
  | { kind: 'unit'; id: string; layer: string; parent?: string; groupMember: boolean }
  | { kind: 'underlay'; id: string };
let layerDrag: LayerDrag | null = null;

// Header glyphs: 24 × 24 line drawings in currentColor, like the toolbar icons.
const LAYER_GLYPH = {
  open: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
  folded: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>',
  eye: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>',
  eyeOff: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3l18 18"/><path d="M10.6 5.1A10.5 10.5 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4.1M6.6 6.6C3.8 8.4 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>',
  lock: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>',
  unlock: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 7.5-2"/></svg>',
};

/** Every layer edit is one undo step and re-reads the layers. */
function editLayers(fn: () => void): void {
  pushHistory();
  fn();
  dropLayerCache();
  renderProperties();
  invalidate();
}

/** Show / hide or lock / unlock a layer. A hidden or locked layer can't stay
 *  the current one (nothing drawn there could be seen or selected). */
function toggleLayerFlag(l: DrawingLayer, flag: 'hidden' | 'locked'): void {
  const on = !l[flag];
  editLayers(() => {
    setLayerFlag(state.drawing, LAYER_ENV, l.id, flag, on);
    if (on && partsTab.currentLayerId === l.id) partsTab.currentLayerId = null;
    if (l.home === 'imported') redrawBackdrop();
  });
}

/** Next unused "Layer N" name. */
/** A new, empty layer — above `aboveId`, else above the selection's layer, else
 *  right under the top layer (so the dimensions stay on top) — renamed in place. */
function newLayer(aboveId?: string): void {
  const m = layers();
  const sel = [...selectedObjectIds()][0];
  const top = m.layers[m.layers.length - 1];
  const anchor = aboveId ?? (sel ? m.layerOf.get(sel) : undefined) ?? (m.layers.length > 2 ? m.layers[m.layers.length - 2].id : top.id);
  const id = newId('layer');
  editLayers(() => { addLayer(state.drawing, LAYER_ENV, id, undefined, anchor); });
  const nameEl = $('properties-body').querySelector<HTMLElement>(`.layer-head[data-layer="${id}"] .layer-name`);
  const l = layerById(id);
  if (nameEl && l) startLayerRename(l, nameEl, true);
}

function startLayerRename(l: DrawingLayer, nameEl: HTMLElement, isNew = false): void {
  startRowRename(nameEl, l.name ?? '', layerLabel({ ...l, name: undefined }), (v) => { renameLayer(state.drawing, LAYER_ENV, l.id, v); }, isNew);
}

/** Delete a layer AND everything on it, after asking. One undo step. */
async function deleteLayerAndContents(l: DrawingLayer): Promise<void> {
  const m = layers();
  const ids = layerObjectIds(m, l.id).filter((id) => !id.startsWith('table:'));
  const msg = tr('Delete the layer “{name}” and the {n} object(s) on it?').replace('{name}', layerLabel(l)).replace('{n}', String(ids.length));
  if (ids.length && !(await showConfirm(msg, { ok: 'Delete', danger: true }))) return;
  pushHistory();
  deleteObjectsById(new Set(ids));
  removeLayer(state.drawing, LAYER_ENV, l.id);
  if (partsTab.currentLayerId === l.id) partsTab.currentLayerId = null;
  dropLayerCache();
  renderProperties();
  invalidate();
}

/** The layer header's right-click menu. */
function showLayerMenu(e: MouseEvent, l: DrawingLayer, nameEl: HTMLElement): void {
  const items: Array<{ label: string; run: () => void; danger?: boolean; disabled?: boolean }> = [
    { label: tr('Rename…'), run: () => startLayerRename(l, nameEl) },
  ];
  if (l.home !== 'imported') {
    items.push(partsTab.currentLayerId === l.id
      ? { label: tr('Stop drawing on this layer'), run: () => { partsTab.currentLayerId = null; renderProperties(); } }
      : { label: tr('Draw new objects on this layer'), disabled: !!(l.hidden || l.locked), run: () => { partsTab.currentLayerId = l.id; renderProperties(); flashToolStatus(tr('New objects go on “{name}”.').replace('{name}', layerLabel(l))); } });
    items.push({ label: tr('New layer above'), run: () => newLayer(l.id) });
    // Only what a selection can hold (not its dimensions, sections, connections,
    // floorings or tables): with nothing of that, there is nothing to select.
    const all = selectionOfIds(state.drawing, layerObjectIds(layers(), l.id));
    const any = Object.values(all).some((a) => Array.isArray(a) && a.length > 0);
    items.push({
      label: tr('Select everything on this layer'), disabled: !!(l.hidden || l.locked) || !any,
      run: () => selectObjectFromList(() => setObjectSelection(all)),
    });
    items.push({ label: tr('Delete layer…'), danger: true, run: () => { void deleteLayerAndContents(l); } });
  }
  const menu = $('ctx-menu');
  menu.innerHTML = '';
  for (const it of items) {
    const b = document.createElement('button');
    b.textContent = '   ' + it.label;
    if (it.danger) b.className = 'danger';
    if (it.disabled) b.disabled = true;
    b.addEventListener('click', () => { hideCtxMenu(); it.run(); });
    menu.appendChild(b);
  }
  menu.classList.remove('hidden');
  const h = menu.offsetHeight;
  menu.style.left = Math.min(e.clientX, window.innerWidth - 220) + 'px';
  menu.style.top = Math.max(4, Math.min(e.clientY, window.innerHeight - h - 4)) + 'px';
}

/** Where on a row the pointer is: its upper half drops OVER it (the list is top
 *  first, so higher in the list paints later). */
function dropOver(row: HTMLElement, e: DragEvent): boolean {
  const r = row.getBoundingClientRect();
  return e.clientY < r.top + r.height / 2;
}

/** A layer's header: fold, show / hide, lock, its name, and the drag that
 *  restacks the layers (Imported stays at the bottom). Items dropped on it land
 *  on top of that layer. */
function layerHeader(body: HTMLElement, l: DrawingLayer, count: number): void {
  const head = document.createElement('div');
  head.className = 'prop-section-title layer-head'
    + (l.hidden ? ' layer-hidden' : '') + (l.locked ? ' layer-locked' : '') + (l.id === partsTab.currentLayerId ? ' layer-current' : '');
  head.dataset.layer = l.id;
  head.dataset.count = String(count);
  const folded = layersCollapsed.has(l.id);
  const button = (cls: string, svg: string, title: string, on: boolean, run: () => void): HTMLButtonElement => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `layer-btn ${cls}` + (on ? ' on' : '');
    b.innerHTML = svg;
    b.title = title;
    b.addEventListener('click', (ev) => { ev.stopPropagation(); run(); });
    return b;
  };
  const fold = button('layer-fold', folded ? LAYER_GLYPH.folded : LAYER_GLYPH.open, folded ? tr('Expand') : tr('Collapse'), false, () => {
    if (layersCollapsed.has(l.id)) layersCollapsed.delete(l.id); else layersCollapsed.add(l.id);
    renderProperties();
  });
  const eye = button('layer-eye', l.hidden ? LAYER_GLYPH.eyeOff : LAYER_GLYPH.eye, l.hidden ? tr('Show layer') : tr('Hide layer'), !!l.hidden, () => toggleLayerFlag(l, 'hidden'));
  const lock = button('layer-lock', l.locked ? LAYER_GLYPH.lock : LAYER_GLYPH.unlock, l.locked ? tr('Unlock layer') : tr('Lock layer'), !!l.locked, () => toggleLayerFlag(l, 'locked'));
  const name = document.createElement('span');
  name.className = 'layer-name';
  name.textContent = layerLabel(l);
  name.addEventListener('dblclick', (ev) => { ev.stopPropagation(); startLayerRename(l, name); });
  head.append(fold, eye, lock, name);
  head.addEventListener('contextmenu', (ev) => { ev.preventDefault(); ev.stopPropagation(); showLayerMenu(ev, l, name); });

  const pinned = l.home === 'imported';
  if (!pinned) {
    head.draggable = true;
    head.addEventListener('dragstart', (ev) => {
      layerDrag = { kind: 'layer', id: l.id };
      ev.dataTransfer?.setData('text/plain', l.id);
      if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
      head.classList.add('parts-dragging');
    });
    head.addEventListener('dragend', () => { layerDrag = null; head.classList.remove('parts-dragging'); });
  }
  const clear = (): void => head.classList.remove('drop-above', 'drop-below', 'drop-into');
  const accepts = (): boolean => {
    const d = layerDrag;
    if (!d || pinned) return false;
    if (d.kind === 'layer') return d.id !== l.id;
    if (d.kind === 'unit') return !d.groupMember && d.layer !== l.id;
    return false;
  };
  // An item dropped on the header moves onto this layer; a LAYER being dragged
  // is placed between the cards (`installLayerGapDnD`), so it is left alone here.
  head.addEventListener('dragover', (ev) => {
    if (layerDrag?.kind !== 'unit' || !accepts()) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
    head.classList.add('drop-into');
  });
  head.addEventListener('dragleave', clear);
  head.addEventListener('drop', (ev) => {
    const d = layerDrag;
    clear();
    if (d?.kind !== 'unit' || !accepts()) return;
    ev.preventDefault();
    ev.stopPropagation();
    layerDrag = null;
    editLayers(() => moveToLayer(state.drawing, LAYER_ENV, [d.id], l.id));
  });
  body.appendChild(head);
  installLayerGapDnD();
}

/**
 * A layer dragged in the Parts tab lands BETWEEN the layer cards: wherever the
 * pointer is (over a card, its rows or the gap), the drop goes into the gap
 * nearest it, shown as a line in that gap (`layer-gap-above` / `-below` on the
 * card it borders). Imported stays at the bottom, so nothing goes under it.
 */
let layerGapDnDInstalled = false;
function installLayerGapDnD(): void {
  if (layerGapDnDInstalled) return;
  layerGapDnDInstalled = true;
  const body = $('properties-body');
  const clear = (): void => body.querySelectorAll('.layer-gap-above, .layer-gap-below').forEach((c) => c.classList.remove('layer-gap-above', 'layer-gap-below'));
  /** The gap the pointer is nearest: the card it sits above (or the last card it
   *  sits below), and the layer index (bottom first) the drop moves to. */
  const target = (ev: DragEvent): { card: HTMLElement; below: boolean; at: number } | null => {
    const d = layerDrag;
    if (d?.kind !== 'layer') return null;
    const cards = Array.from(body.querySelectorAll<HTMLElement>('.prop-card'))
      .map((card) => ({ card, id: card.querySelector<HTMLElement>(':scope > .layer-head')?.dataset.layer }))
      .filter((c): c is { card: HTMLElement; id: string } => !!c.id && c.id !== d.id); // top first
    if (!cards.length) return null;
    let i = cards.findIndex((c) => { const r = c.card.getBoundingClientRect(); return ev.clientY < r.top + r.height / 2; });
    if (i < 0) i = cards.length;
    const imported = layers().layers[0].id;
    if (i === cards.length && cards[i - 1].id === imported) i--; // never under Imported
    const rest = layers().layers.filter((x) => x.id !== d.id);
    if (i < cards.length) return { card: cards[i].card, below: false, at: rest.findIndex((x) => x.id === cards[i].id) + 1 };
    return { card: cards[i - 1].card, below: true, at: rest.findIndex((x) => x.id === cards[i - 1].id) };
  };
  body.addEventListener('dragover', (ev) => {
    const t = target(ev);
    if (!t) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
    if (!t.card.classList.contains(t.below ? 'layer-gap-below' : 'layer-gap-above')) {
      clear();
      t.card.classList.add(t.below ? 'layer-gap-below' : 'layer-gap-above');
    }
  });
  body.addEventListener('dragleave', (ev) => { if (!body.contains(ev.relatedTarget as Node | null)) clear(); });
  body.addEventListener('dragend', clear);
  body.addEventListener('drop', (ev) => {
    const t = target(ev);
    const d = layerDrag;
    clear();
    if (!t || d?.kind !== 'layer') return;
    ev.preventDefault();
    layerDrag = null;
    editLayers(() => moveLayer(state.drawing, LAYER_ENV, d.id, t.at));
  });
}

/** Make an item's row draggable, and a drop target for its siblings (restack)
 *  or for any loose item (it moves onto this row's layer, next to it). A group's
 *  member only restacks inside its group: a group is one item. */
function layerRowDnD(row: HTMLElement, spec: { id: string; layer: string; parent?: string; groupMember: boolean }): void {
  row.draggable = true;
  const clear = (): void => row.classList.remove('drop-above', 'drop-below');
  const accepts = (): boolean => {
    const d = layerDrag;
    if (!d || d.kind !== 'unit' || d.id === spec.id) return false;
    if (d.layer === spec.layer && d.parent === spec.parent) return true; // siblings
    if (d.groupMember) return false;
    // What stands in a room stays nested on the room's layer: its top level
    // can't take it (it would land straight back in the room).
    if (d.parent !== undefined && d.layer === spec.layer) return false;
    return spec.parent === undefined; // onto another layer's (or the top level's) row
  };
  row.addEventListener('dragstart', (ev) => {
    ev.stopPropagation();
    layerDrag = { kind: 'unit', ...spec };
    ev.dataTransfer?.setData('text/plain', spec.id);
    if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
    row.classList.add('parts-dragging');
  });
  row.addEventListener('dragend', () => { layerDrag = null; row.classList.remove('parts-dragging'); clear(); });
  row.addEventListener('dragover', (ev) => {
    if (!accepts()) return;
    ev.preventDefault();
    ev.stopPropagation();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
    const up = dropOver(row, ev);
    row.classList.toggle('drop-above', up);
    row.classList.toggle('drop-below', !up);
  });
  row.addEventListener('dragleave', clear);
  row.addEventListener('drop', (ev) => {
    const d = layerDrag;
    clear();
    if (!accepts() || !d || d.kind !== 'unit') return;
    ev.preventDefault();
    ev.stopPropagation();
    layerDrag = null;
    const at: Placement = dropOver(row, ev) ? { over: spec.id } : { under: spec.id };
    if (d.layer === spec.layer && d.parent === spec.parent) editLayers(() => restack(state.drawing, LAYER_ENV, [d.id], at));
    else editLayers(() => moveToLayer(state.drawing, LAYER_ENV, [d.id], spec.layer, at));
  });
}

/** An underlay row restacks among the Imported layer's other underlays (their
 *  array order is their paint order, last on top). */
function underlayRowDnD(row: HTMLElement, id: string): void {
  row.draggable = true;
  const clear = (): void => row.classList.remove('drop-above', 'drop-below');
  const accepts = (): boolean => layerDrag?.kind === 'underlay' && layerDrag.id !== id;
  row.addEventListener('dragstart', (ev) => {
    layerDrag = { kind: 'underlay', id };
    ev.dataTransfer?.setData('text/plain', id);
    if (ev.dataTransfer) ev.dataTransfer.effectAllowed = 'move';
    row.classList.add('parts-dragging');
  });
  row.addEventListener('dragend', () => { layerDrag = null; row.classList.remove('parts-dragging'); clear(); });
  row.addEventListener('dragover', (ev) => {
    if (!accepts()) return;
    ev.preventDefault();
    const up = dropOver(row, ev);
    row.classList.toggle('drop-above', up);
    row.classList.toggle('drop-below', !up);
  });
  row.addEventListener('dragleave', clear);
  row.addEventListener('drop', (ev) => {
    const d = layerDrag;
    clear();
    if (!accepts() || !d || d.kind !== 'underlay') return;
    ev.preventDefault();
    layerDrag = null;
    const arr = state.drawing.backgroundImages ?? [];
    const moved = arr.find((b) => b.id === d.id);
    if (!moved) return;
    const rest = arr.filter((b) => b !== moved);
    const i = rest.findIndex((b) => b.id === id);
    if (i < 0) return;
    editLayers(() => {
      rest.splice(dropOver(row, ev) ? i + 1 : i, 0, moved);
      state.drawing.backgroundImages = rest;
      redrawBackdrop();
    });
  });
}

/** Add one tree node (chevron + coloured dot + label) and return its children
 *  container, so callers can nest further nodes under it. */
function partsTreeNode(parent: HTMLElement, opts: {
  key: string; label: string; color?: string; active?: boolean;
  hoverId?: string; hoverGroupId?: string; hoverHeaterWall?: { instId: string; wallId: string };
  hoverInnerWall?: { instId: string; wallId: string };
  /** World ring of the room feature this row names, highlighted while hovered. */
  hoverRing?: { key: string; ring: () => Vec2[] };
  onClick?: () => void; hasChildren?: boolean;
  trailing?: HTMLElement; onContext?: (e: MouseEvent, nameEl: HTMLElement) => void; dim?: boolean;
  /** Makes the row draggable to restack the item it stands for. */
  drag?: { id: string; layer: string; parent?: string; groupMember: boolean };
}): HTMLElement {
  const node = document.createElement('div');
  node.className = 'tree-node';
  const row = document.createElement('div');
  row.className = 'part-row tree-row' + (opts.active ? ' active' : '');
  const twisty = document.createElement('span');
  twisty.className = 'tree-twisty' + (opts.hasChildren ? '' : ' leaf');
  const kids = document.createElement('div');
  kids.className = 'tree-children';
  const apply = (): void => {
    const collapsed = partsCollapsed.has(opts.key);
    twisty.textContent = opts.hasChildren ? (collapsed ? '▸' : '▾') : '';
    kids.style.display = collapsed ? 'none' : '';
  };
  if (opts.hasChildren) {
    twisty.addEventListener('click', (e) => {
      e.stopPropagation();
      if (partsCollapsed.has(opts.key)) partsCollapsed.delete(opts.key); else partsCollapsed.add(opts.key);
      apply();
    });
  }
  const dot = document.createElement('span');
  dot.className = 'disc-dot';
  if (opts.color) dot.style.background = opts.color; else dot.style.visibility = 'hidden';
  const name = document.createElement('span');
  name.className = 'part-name';
  name.textContent = opts.label;
  if (opts.dim) name.style.opacity = '0.55';
  row.append(twisty, dot, name);
  if (opts.trailing) row.appendChild(opts.trailing);
  if (opts.onClick) row.addEventListener('click', opts.onClick);
  if (opts.onContext) row.addEventListener('contextmenu', (e) => { e.preventDefault(); e.stopPropagation(); opts.onContext!(e, name); });
  if (opts.hoverId) {
    const hid = opts.hoverId;
    row.addEventListener('mouseenter', () => { hoverPartId = hid; invalidate(); });
    row.addEventListener('mouseleave', () => { if (hoverPartId === hid) { hoverPartId = null; invalidate(); } });
  }
  if (opts.hoverGroupId) {
    const gid = opts.hoverGroupId;
    row.addEventListener('mouseenter', () => { hoverGroupId = gid; invalidate(); });
    row.addEventListener('mouseleave', () => { if (hoverGroupId === gid) { hoverGroupId = null; invalidate(); } });
  }
  if (opts.hoverInnerWall) {
    const iw = opts.hoverInnerWall;
    row.addEventListener('mouseenter', () => { hoverInnerWall = iw; invalidate(); });
    row.addEventListener('mouseleave', () => { if (hoverInnerWall?.wallId === iw.wallId) { hoverInnerWall = null; invalidate(); } });
  }
  if (opts.hoverHeaterWall) {
    const hw = opts.hoverHeaterWall;
    row.addEventListener('mouseenter', () => { hoverHeaterWall = hw; invalidate(); });
    row.addEventListener('mouseleave', () => { if (hoverHeaterWall?.wallId === hw.wallId) { hoverHeaterWall = null; invalidate(); } });
  }
  if (opts.hoverRing) {
    const hr = opts.hoverRing;
    row.addEventListener('mouseenter', () => { hoverFeatureRing = hr; invalidate(); });
    row.addEventListener('mouseleave', () => { if (hoverFeatureRing?.key === hr.key) { hoverFeatureRing = null; invalidate(); } });
  }
  if (opts.drag) layerRowDnD(row, opts.drag);
  apply();
  node.append(row, kids);
  parent.appendChild(node);
  return kids;
}

/** Nested features of a room shown as child rows: glass panes, openings. */
/** Room feature rows for the Parts tab: the glazed panes and the openings. `ring`
 *  is the feature's own world outline, used both for the hover highlight and for
 *  Zoom to, so a row never highlights or frames its host room instead. The segment
 *  is captured by reference, so Delete removes the exact pane the row stands for
 *  even if the array has shifted. */
function roomFeatureRows(inst: ProductInstance): Array<{ key: string; label: string; color: string; ring: () => Vec2[]; del: () => void; glassId?: string; openingId?: string }> {
  const out: Array<{ key: string; label: string; color: string; ring: () => Vec2[]; del: () => void; glassId?: string; openingId?: string }> = [];
  const glass = glassPanesOf(inst);
  glass.forEach((seg, i) => out.push({
    key: `glass:${inst.id}:${i}`,
    // Say which wall it is glazed into when it is a partition, so two panes on
    // different walls aren't just "Glass 1" and "Glass 2".
    label: (glass.length > 1 ? `${tr('Glass')} ${i + 1}` : tr('Glass')) + (seg.innerWallId != null ? ` ${tr('(inner wall)')}` : ''),
    color: seg.color ?? '#8fd0ff',
    glassId: seg.id,
    // glassPolysLocal, not polyGlassPolys: it is the one dispatch that also knows
    // about a pane glazed into a partition.
    ring: () => glassPolysLocal(inst, seg).flat().map((p) => instanceToWorld(inst, p)),
    del: () => {
      pushHistory();
      inst.glass = (inst.glass || []).filter((s) => s !== seg);
      if (!inst.glass.length) delete inst.glass;
      regenerateSauna(inst); // heals the pane's jambs
      renderProperties();
      invalidate();
    },
  }));
  const openings = openingsOf(inst);
  openings.forEach((seg, i) => out.push({
    key: `opening:${inst.id}:${i}`,
    label: openings.length > 1 ? `${tr('Opening')} ${i + 1}` : tr('Opening'), color: '#9fb0c4',
    openingId: seg.id,
    ring: () => polyOpeningBand(inst, seg).map((p) => instanceToWorld(inst, p)),
    del: () => {
      pushHistory();
      inst.openings = (inst.openings || []).filter((s) => s !== seg);
      if (!inst.openings.length) delete inst.openings;
      regenerateSauna(inst); // heals the opening's jambs
      renderProperties();
      invalidate();
    },
  }));
  return out;
}

/** A small snowflake button that freezes / unfreezes an instance in place. */
function freezeToggle(m: ProductInstance): HTMLElement {
  const b = document.createElement('button');
  b.className = 'part-freeze' + (m.frozen ? ' frozen' : '');
  b.textContent = '❄';
  b.title = tr(m.frozen ? 'Frozen — click to unlock' : 'Freeze (lock in place)');
  b.addEventListener('click', (e) => {
    e.stopPropagation();
    if (refuseLockedRow(m.id)) return;
    pushHistory();
    m.frozen = !m.frozen;
    if (m.frozen && state.selectedId === m.id) { state.selectedId = null; state.selectedIds = []; } // a frozen part can't stay selected
    renderProperties();
    invalidate();
  });
  return b;
}

/** Auto-numbered display name per base name, in drawing order: two "Sauna" parts
 *  become "Sauna01" / "Sauna02". Recomputed on every render, so deleting one
 *  renumbers the rest with the same name. */
export function numberedInstanceNames(): Map<string, string> {
  return numberedNames(state.drawing.instances, (i) => productName(i, getUiLang()));
}

/** Sub-select a door from its Parts-tab row: select the room, then mark the door
 *  so it highlights on the plan and the Delete key / door editor act on it. */
function selectDoorFromList(inst: ProductInstance, door: SaunaDoor): void {
  if (instLocked(inst)) { refuseLockedRow(inst.id); return; } // a frozen / locked room can't be selected
  selectPartFromList(inst.id);
  pickDoor(door.id);
  renderProperties();
  invalidate();
}

/** Delete a door from its room (Parts tab › right-click › Delete). Mirrors the
 *  Delete-key path for a sub-selected door. */
function deleteDoorFromList(inst: ProductInstance, door: SaunaDoor): void {
  editRoom(inst, () => {
    inst.doors = roomDoors(inst).filter((d) => d.id !== door.id);
    if (selDoorId() === door.id) dropFeature(state, 'door');
  });
}

/** A row on a hidden or locked layer can't select its object: say why. */
function refuseLockedRow(id: string): boolean {
  const l = layerOfObject(id);
  if (!l || !(l.hidden || l.locked)) return false;
  flashToolStatus((l.hidden ? tr('“{name}” is hidden.') : tr('“{name}” is locked.')).replace('{name}', layerLabel(l)));
  return true;
}

/** The labels the rows use, numbered once per render. */
export interface RowNames { insts: Map<string, string>; polys: Map<string, string>; walls: Map<string, string>; floors: Map<string, string>; texts: Map<string, string>; dims: Map<string, string>; leaders: Map<string, string>; arcs: Map<string, string>; clouds: Map<string, string>; conns: Map<string, string> }
export function rowNames(): RowNames {
  const d = state.drawing;
  const firstLine = (s: string, fallback: string): string => {
    const t = (s || '').split('\n')[0].trim();
    return t ? (t.length > 28 ? t.slice(0, 28) + '…' : t) : fallback;
  };
  // The kinds' words in the UI's language: the Parts tab is the app's chrome.
  const lang = getUiLang(), word = (key: string): string => trIn(key, lang);
  return {
    insts: numberedInstanceNames(),
    polys: numberedNames(d.polygons || [], (p) => objectName(p.name, polygonWord(p), lang, trIn)),
    walls: numberedNames(d.walls || [], (w) => objectName(w.name, wallWord(w), lang, trIn)),
    floors: numberedNames(d.floorings || [], (f) => objectName(f.name, floorLabel(f.kind), lang, trIn)),
    texts: numberedNames(d.texts || [], (t) => firstLine(t.text, word('Text'))),
    dims: numberedNames(d.dimensions || [], () => word('Dimension')),
    leaders: numberedNames(d.leaders || [], (l) => firstLine(l.text, word('Leader'))),
    arcs: numberedNames(d.arcs || [], () => word('Arc')),
    clouds: numberedNames(d.revisionClouds || [], () => word('Revision cloud')),
    conns: scheduleNumbers(connectionSchedule(d)),
  };
}

/** One item of a layer (its whole subtree), as Parts-tab rows. */
function layerUnitRow(parent: HTMLElement, u: LayerUnit, layerId: string, names: RowNames, parentId?: string, groupMember = false): void {
  const drag = { id: u.id, layer: layerId, parent: parentId, groupMember };
  if (u.kind === 'group') { addGroupTreeNode(parent, u, layerId, names, drag); return; }
  if (u.kind === 'instance') { addInstanceTreeNode(parent, u, layerId, names, drag); return; }
  const d = state.drawing;
  const o = u.item.obj;
  const id = u.id;
  const sel = selectedObjectIds();
  const pick = (apply: () => void) => (): void => { if (!refuseLockedRow(id)) selectObjectFromList(apply); };
  const menu = (zoom: Vec2[] | null, rename?: (nameEl: HTMLElement, label: string) => void, del: (() => void) | null = null) =>
    (e: MouseEvent, nameEl: HTMLElement): void => {
      showObjectRowMenu(e, {
        zoomTo: zoom && zoom.length ? () => zoomToPoints(zoom) : undefined,
        rename: rename ? () => rename(nameEl, nameEl.textContent ?? '') : undefined,
        del: del ?? undefined,
        lockId: id,
      });
    };
  let row: { label: string; color: string; active: boolean; onClick: () => void; onContext: (e: MouseEvent, nameEl: HTMLElement) => void } | null = null;
  switch (u.kind) {
    case 'polygon': {
      const select = (): void => { if (!refuseLockedRow(id)) selectPolygonFromList(id); };
      row = {
        label: names.polys.get(id) ?? tr(polygonWord(o)), color: o.fill || o.stroke || '#8a8f98',
        active: sel.has(id), onClick: select,
        onContext: menu(o.points, (el, label) => startRowRename(el, o.name ?? '', label, (v) => { o.name = v || undefined; }), () => { selectPolygonFromList(id); deleteSelected(); }),
      };
      break;
    }
    case 'wall': {
      const select = (): void => { if (!refuseLockedRow(id)) selectWallFromList(id); };
      row = {
        label: names.walls.get(id) ?? tr(wallWord(o)), color: o.stroke || o.fill || '#8a8f98',
        active: sel.has(id), onClick: select,
        onContext: menu(o.points, (el, label) => startRowRename(el, o.name ?? '', label, (v) => { o.name = v || undefined; }), () => { selectWallFromList(id); deleteSelected(); }),
      };
      break;
    }
    case 'flooring': {
      const select = pick(() => selectOne(state, 'flooring', id));
      row = {
        label: names.floors.get(id) ?? floorLabel(o.kind), color: FLOOR_DOT[o.kind as FloorKind] ?? '#8a8f98',
        active: id === state.selectedFloorId, onClick: select,
        onContext: menu(o.points, (el, label) => startRowRename(el, o.name ?? '', label, (v) => { o.name = v || undefined; }), () => { selectObjectFromList(() => selectOne(state, 'flooring', id)); deleteSelected(); }),
      };
      break;
    }
    case 'text': {
      const select = pick(() => selectOne(state, 'text', id));
      row = {
        label: names.texts.get(id) ?? 'Text', color: o.textColor ?? ANNOTATION_COLOR, active: sel.has(id), onClick: select,
        onContext: menu(textBoxCorners(o), undefined, () => { selectObjectFromList(() => selectOne(state, 'text', id)); deleteSelected(); }),
      };
      break;
    }
    case 'dim': {
      const select = pick(() => selectOne(state, 'dim', id));
      row = {
        label: names.dims.get(id) ?? 'Dimension', color: o.color ?? ANNOTATION_COLOR, active: id === state.selectedDimId, onClick: select,
        onContext: menu([o.a, o.b], undefined, () => { selectObjectFromList(() => selectOne(state, 'dim', id)); deleteSelected(); }),
      };
      break;
    }
    case 'section': {
      const idx = (d.sections || []).findIndex((x) => x.id === id);
      const select = pick(() => selectOne(state, 'section', id));
      row = {
        label: `${tr('Section')} ${sectionLabel(idx)}`, color: ANNOTATION_COLOR, active: id === state.selectedSectionId, onClick: select,
        onContext: menu([o.a, o.b], undefined, () => { selectObjectFromList(() => selectOne(state, 'section', id)); deleteSelected(); }),
      };
      break;
    }
    case 'rcp': {
      const select = pick(() => selectOne(state, 'rcp', id));
      row = {
        label: `${tr((o as CeilingPlanView).kind === 'above' ? 'Plan above the ceiling' : 'Reflected ceiling plan')} · ${names.insts.get((o as CeilingPlanView).roomId) ?? ''}`, color: ANNOTATION_COLOR, active: id === state.selectedRcpId, onClick: select,
        onContext: menu(rcpViewOutline(o as CeilingPlanView), undefined, () => { selectObjectFromList(() => selectOne(state, 'rcp', id)); deleteSelected(); }),
      };
      break;
    }
    case 'leader': {
      const select = pick(() => selectOne(state, 'leader', id));
      row = {
        label: names.leaders.get(id) ?? 'Leader', color: o.color ?? ANNOTATION_COLOR, active: sel.has(id), onClick: select,
        onContext: menu(o.points, undefined, () => { selectObjectFromList(() => selectOne(state, 'leader', id)); deleteSelected(); }),
      };
      break;
    }
    case 'arc': {
      const select = pick(() => selectOne(state, 'arc', id));
      row = {
        label: names.arcs.get(id) ?? 'Arc', color: o.stroke || ANNOTATION_COLOR, active: sel.has(id), onClick: select,
        onContext: menu(arcSamplePts(o), undefined, () => { selectObjectFromList(() => selectOne(state, 'arc', id)); deleteSelected(); }),
      };
      break;
    }
    case 'cloud': {
      const select = pick(() => selectOne(state, 'cloud', id));
      row = {
        label: names.clouds.get(id) ?? 'Revision cloud', color: o.color ?? '#e05a5a', active: sel.has(id), onClick: select,
        onContext: menu(o.points, undefined, () => { selectObjectFromList(() => selectOne(state, 'cloud', id)); deleteSelected(); }),
      };
      break;
    }
    case 'conn': {
      const num = names.conns.get(id);
      const select = pick(() => selectOne(state, 'conn', id));
      row = {
        label: num ? `${tr('Connection')} ${num}` : tr('Connection'), color: CONNECTION_COLORS[connDiscipline(o)],
        active: id === state.selectedConnId, onClick: select,
        onContext: menu(connectionPath(o) ?? null, undefined, () => { selectObjectFromList(() => selectOne(state, 'conn', id)); deleteSelected(); }),
      };
      break;
    }
    case 'table': {
      const kind = schedKindOf(id) ?? 'connection';
      const label = tr(SCHED_TABLES[kind].label);
      const select = (): void => {
        if (refuseLockedRow(id)) return;
        setTool('select');
        selectOne(state, 'table', kind);
        renderProperties();
        invalidate();
      };
      row = {
        label, color: ANNOTATION_COLOR, active: state.selectedScheduleTable === kind, onClick: select,
        onContext: (e) => showObjectRowMenu(e, { zoomTo: () => { const r = objectOutline(id); if (r) zoomToPoints(r); } }),
      };
      break;
    }
    default:
      unreachable(u.kind, 'Parts-tab row kind');
  }
  if (!row) return;
  partsTreeNode(parent, {
    key: '', label: row.label, color: row.color, active: row.active, hoverId: id,
    onClick: row.onClick, onContext: row.onContext, drag,
  });
}

function addInstanceTreeNode(parent: HTMLElement, u: LayerUnit, layerId: string, names: RowNames,
  drag?: { id: string; layer: string; parent?: string; groupMember: boolean }): void {
  if (u.kind !== 'instance') return;
  const m = u.item.obj as ProductInstance;
  const label = names.insts.get(m.id) ?? m.name;
  const feats = roomFeatureRows(m);
  const doors = m.autoBenches ? roomDoors(m) : []; // doors are a room feature
  const heaters = heaterWallsOf(m); // hidden heater walls are room features too
  const partitions = m.autoBenches && isPolyRoom(m) ? innerWallsOf(m) : [];
  const supports = m.benchSupports ?? [];
  const backrests = m.autoBenches ? backrestsOf(m) : [];
  const sculpted = m.autoBenches ? sculptedBenchesOf(m) : [];
  const strips = m.autoBenches ? stripsOf(m) : [];
  const spots = m.autoBenches ? spotsOf(m) : [];
  const channels = m.autoBenches ? channelsOf(m) : [];
  const contents = [...u.children].reverse(); // what stands in it, top first
  const kids = partsTreeNode(parent, {
    key: `i:${m.id}`, label, color: traitsOf(m.category).color,
    active: m.id === state.selectedId, hoverId: m.id,
    hasChildren: feats.length > 0 || doors.length > 0 || heaters.length > 0 || partitions.length > 0 || supports.length > 0 || backrests.length > 0 || sculpted.length > 0 || strips.length > 0 || spots.length > 0 || channels.length > 0 || !!(m.autoBenches && coveOf(m)) || contents.length > 0,
    onClick: () => { if (!m.frozen && !refuseLockedRow(m.id)) selectPartFromList(m.id); }, // frozen / locked parts aren't selectable
    onContext: (e, nameEl) => showPartRowMenu(e, m, nameEl),
    trailing: freezeToggle(m), dim: m.frozen, drag,
  });
  // Doors: each a selectable child row with a right-click menu (Zoom to / Delete).
  doors.forEach((d, i) => {
    // The door's own swing footprint drives both the hover highlight and Zoom to —
    // pointing either at the host room highlights/frames the whole sauna instead.
    const ring = (): Vec2[] => { const rd = roomDoor(m, d); return rd ? doorFootprint(rd).map((p) => instanceToWorld(m, p)) : []; };
    partsTreeNode(kids, {
      key: '', label: doors.length > 1 ? `${tr('Door')} ${i + 1}` : tr('Door'), color: '#c8a24a',
      hoverRing: { key: `door:${m.id}:${d.id ?? i}`, ring },
      active: m.id === state.selectedId && d.id === selDoorId(),
      onClick: () => selectDoorFromList(m, d),
      onContext: (e) => showObjectRowMenu(e, {
        zoomTo: () => { const r = ring(); if (r.length >= 2) zoomToPoints(r); },
        del: () => deleteDoorFromList(m, d),
        lockId: m.id,
      }),
    });
  });
  // Hidden heater walls: selectable rows with their own hover highlight and menu.
  heaters.forEach((h, i) => {
    partsTreeNode(kids, {
      key: '', label: heaters.length > 1 ? `${tr('Heater wall')} ${i + 1}` : tr('Heater wall'), color: h.color,
      hoverHeaterWall: { instId: m.id, wallId: h.id },
      active: featureOf(state, 'heaterWall')?.wallId === h.id,
      onClick: () => {
        if (refuseLockedRow(m.id)) return;
        selectPartFromList(m.id);
        selectFeature(state, { kind: 'heaterWall', instId: m.id, wallId: h.id });
        renderProperties();
        invalidate();
      },
      onContext: (e) => showObjectRowMenu(e, {
        zoomTo: () => {
          const band = heaterWallBands(m).find((b) => b.wall.id === h.id);
          if (band) zoomToPoints(band.poly.map((p) => instanceToWorld(m, p)));
        },
        del: () => {
          editRoom(m, () => removeHeaterWall(m, h.id));
        },
        lockId: m.id,
      }),
    });
  });
  // Interior partitions: selectable rows, same hover highlight and menu.
  partitions.forEach((iw, i) => {
    partsTreeNode(kids, {
      key: '', label: partitions.length > 1 ? `${tr('Inner wall')} ${i + 1}` : tr('Inner wall'), color: iw.color ?? defaultWallColor(m),
      hoverInnerWall: { instId: m.id, wallId: iw.id! },
      active: featureOf(state, 'innerWall')?.wallId === iw.id,
      onClick: () => {
        if (refuseLockedRow(m.id)) return;
        selectPartFromList(m.id);
        selectFeature(state, { kind: 'innerWall', instId: m.id, wallId: iw.id! });
        renderProperties();
        invalidate();
      },
      onContext: (e) => showObjectRowMenu(e, {
        zoomTo: () => {
          const band = polyInnerWallBand(m, iw);
          if (band.length >= 4) zoomToPoints(band.map((p) => instanceToWorld(m, p)));
        },
        del: () => {
          editRoom(m, () => removeInnerWall(m, iw.id!));
        },
        lockId: m.id,
      }),
    });
  });
  // Bench supports: each outlines and frames its own trestles.
  supports.forEach((b, i) => {
    const ring = (): Vec2[] => {
      const pts = supportMarksOf(m).filter((k) => k.support === b.id).flatMap((k) => k.poly).map((p) => supportWorld(m, p));
      if (!pts.length) return [];
      const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
      const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
      return [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];
    };
    partsTreeNode(kids, {
      key: '', label: `${tr('Bench support')} ${i + 1}`, color: '#8c6a46',
      hoverRing: { key: `support:${m.id}:${b.id}`, ring },
      active: featureOf(state, 'support')?.instId === m.id && featureOf(state, 'support')?.id === b.id && m.id === state.selectedId,
      onClick: () => {
        if (refuseLockedRow(m.id)) return;
        selectPartFromList(m.id);
        selectFeature(state, { kind: 'support', instId: m.id, id: b.id });
        renderProperties();
        invalidate();
      },
      onContext: (e) => showObjectRowMenu(e, {
        zoomTo: () => { const r = ring(); if (r.length >= 2) zoomToPoints(r); },
        del: () => { if (m.frozen) return; pushHistory(); removeSupport(m, b.id); renderProperties(); invalidate(); },
        lockId: m.id,
      }),
    });
  });
  // Backrests and strip lights: each outlines and frames itself.
  const fixtureRow = (id: string, label: string, color: string): void => {
    const ring = (): Vec2[] => (fixtureOutline(m, id) ?? []).map((p) => instanceToWorld(m, p));
    partsTreeNode(kids, {
      key: '', label, color,
      hoverRing: { key: `fixture:${m.id}:${id}`, ring },
      active: featureOf(state, 'fixture')?.instId === m.id && featureOf(state, 'fixture')?.id === id && m.id === state.selectedId,
      onClick: () => {
        if (refuseLockedRow(m.id)) return;
        selectPartFromList(m.id);
        selectFeature(state, { kind: 'fixture', instId: m.id, id });
        renderProperties();
        invalidate();
      },
      onContext: (e) => showObjectRowMenu(e, {
        zoomTo: () => { const r = ring(); if (r.length >= 2) zoomToPoints(r); },
        del: () => { if (m.frozen) return; pushHistory(); removeFixture(m, id); renderProperties(); invalidate(); },
        lockId: m.id,
      }),
    });
  };
  backrests.forEach((br, i) => fixtureRow(br.id, `${tr('Backrest')} ${i + 1}`, backrestColor(m, br)));
  sculpted.forEach((sb, i) => fixtureRow(sb.id, `${tr('Sculpted bench')} ${i + 1}`, sculptColor(m, sb)));
  strips.forEach((l) => fixtureRow(l.id, `${tr('Strip light')} ${l.pos}`, LIGHT_AMBER));
  spots.filter((l) => !l.fibre).forEach((l, i) => fixtureRow(l.id, `${tr('Spot light')} ${i + 1}`, LIGHT_AMBER));
  spots.filter((l) => l.fibre).forEach((l, i) => fixtureRow(l.id, `${tr('Fibre point')} ${i + 1}`, LIGHT_AMBER));
  channels.forEach((l, i) => fixtureRow(l.id, `${tr('Channel light')} ${i + 1}`, LIGHT_AMBER));
  if (m.autoBenches) skiesOf(m).forEach((l) => fixtureRow(l.id, tr('Starry sky'), LIGHT_AMBER));
  const cove = m.autoBenches ? coveOf(m) : null;
  if (cove) fixtureRow(cove.id, tr('Cove light'), LIGHT_AMBER);
  for (const f of feats) {
    // Panes and openings highlight and frame themselves, like the door rows above.
    partsTreeNode(kids, {
      key: '', label: f.label, color: f.color,
      hoverRing: { key: f.key, ring: f.ring },
      active: m.id === state.selectedId && ((f.glassId != null && f.glassId === selGlassId()) || (f.openingId != null && f.openingId === selOpeningId())),
      onClick: () => {
        if (refuseLockedRow(m.id)) return;
        selectPartFromList(m.id);
        // A pane or opening row sub-selects it, so the panel shows its own
        // properties (the same state a click on it in the plan sets).
        if (f.glassId != null) { pickSegments('glass', [f.glassId]); renderProperties(); invalidate(); }
        if (f.openingId != null) { pickSegments('opening', [f.openingId]); renderProperties(); invalidate(); }
      },
      onContext: (e) => showObjectRowMenu(e, {
        zoomTo: () => { const r = f.ring(); if (r.length >= 2) zoomToPoints(r); },
        del: f.del,
        lockId: m.id,
      }),
    });
  }
  // What stands in a room is listed under it, top of the stacking first; each
  // restacks among the room's contents, or drags out onto another layer.
  for (const c of contents) layerUnitRow(kids, c, layerId, names, m.id, false);
}

/** A group's node in the Parts tree: its members and nested groups, top of the
 *  stacking first (a room with what stands in it). A placed product is tagged.
 *  A click selects the whole group, as a click on the drawing does. */
function addGroupTreeNode(parent: HTMLElement, u: LayerUnit, layerId: string, names: RowNames,
  drag: { id: string; layer: string; parent?: string; groupMember: boolean }): void {
  if (u.kind !== 'group') return;
  const gid = u.id;
  const g = u.group;
  const label = groupLabel(gid);
  const members = groupMembers(state.drawing, gid);
  const parts = productParts(gid);
  const tag = document.createElement('span');
  tag.className = 'part-tag';
  tag.textContent = tr('Product');
  const kids = partsTreeNode(parent, {
    key: `g:${gid}`, label, color: traitsOf(parts[0]?.category).color, hasChildren: true,
    hoverGroupId: gid,
    active: members.length > 0 && members.every((m) => selectedObjectIds().has(m.id)),
    onClick: () => { if (!refuseLockedRow(gid)) selectObjectFromList(() => setObjectSelection(groupMembersSel(gid))); },
    onContext: (e, nameEl) => showGroupRowMenu(e, gid, label, nameEl),
    trailing: g?.source ? tag : undefined,
    drag,
  });
  for (const c of [...u.children].reverse()) layerUnitRow(kids, c, layerId, names, gid, true);
}

/** A group's name before its number: the name entered for it, else a placed
 *  product's its product's, else "Group" — in the UI's language (the Parts
 *  tab is chrome). */
function groupBaseName(gid: string): string {
  const g = groupById(state.drawing, gid);
  if (g?.name?.trim()) return g.name;
  const t = g?.source ? state.templates.find((x) => x.id === g.source!.templateId) : undefined;
  return t ? productName(t, getUiLang()) : tr('Group (noun)');
}
/** A group as the Parts tab names it: numbered as every name is (names.md),
 *  but a placed product, unnamed, by its product's name alone. */
function groupLabel(gid: string): string {
  const g = groupById(state.drawing, gid);
  if (g?.source && !g.name?.trim()) return groupBaseName(gid);
  const plain = (state.drawing.groups ?? []).filter((x) => !x.source || x.name?.trim());
  return numberedNames(plain, (x) => groupBaseName(x.id)).get(gid) ?? groupBaseName(gid);
}

/** The Imported layer's rows: the DXF plan (painted over the underlays) and the
 *  underlays, top first. */
function importedRows(parent: HTMLElement): number {
  const imgs = bgImages();
  const hasDxf = !!(state.drawing.background && isFiniteBounds(state.drawing.background.bounds));
  const refuse = (): boolean => {
    const l = importedLayer();
    if (!l.hidden && !l.locked) return false;
    flashToolStatus((l.hidden ? tr('“{name}” is hidden.') : tr('“{name}” is locked.')).replace('{name}', layerLabel(l)));
    return true;
  };
  if (hasDxf) {
    partsTreeNode(parent, {
      key: '', label: tr('Imported drawing (DXF)'), color: '#8a8f98', active: state.editBg, hoverId: '__dxf__',
      onClick: () => { if (!refuse()) selectBackgroundGeom(); },
      onContext: (e) => showObjectRowMenu(e, {
        zoomTo: () => { const b = bgGeomBounds(); if (b) zoomToPoints([b.min, b.max]); },
        del: () => { if (!refuse()) { selectBackgroundGeom(); deleteSelected(); } }, // the DXF underlay has no editable name
      }),
    });
  }
  const indexed = imgs.map((bi, i) => ({ bi, i }));
  for (const { bi, i } of [...indexed].reverse()) {
    const label = bi.name || `${bi.paperWidthMm ? 'PDF' : tr('Image')} ${i + 1}`;
    const kids = partsTreeNode(parent, {
      key: '', label, color: '#5aa9e6', active: state.editPdf && bi.id === state.editPdfId, hoverId: bi.id ?? '',
      onClick: () => { if (!refuse()) selectBackgroundFromList(bi.id ?? null); },
      onContext: (e, nameEl) => showObjectRowMenu(e, {
        zoomTo: () => zoomToPoints(pdfBoxCorners({ x: bi.x, y: bi.y, widthMm: bi.widthMm, heightMm: bi.heightMm, rotation: bi.rotation })),
        rename: () => { if (!refuse()) startRowRename(nameEl, bi.name ?? '', label, (v) => { bi.name = v || undefined; }); },
        del: () => { if (!refuse()) { selectBackgroundFromList(bi.id ?? null); deleteSelected(); } },
      }),
    });
    const row = kids.previousElementSibling as HTMLElement | null;
    if (row && bi.id) underlayRowDnD(row, bi.id);
  }
  return imgs.length + (hasDxf ? 1 : 0);
}

export function renderPartsTab(body: HTMLElement): void {
  hoverPartId = null; // rebuilding the list drops any stale hover highlight
  hoverGroupId = null;
  hoverHeaterWall = null;
  hoverInnerWall = null;
  hoverFeatureRing = null;
  const m = layers();
  if (partsTab.currentLayerId && !m.layers.some((l) => l.id === partsTab.currentLayerId)) partsTab.currentLayerId = null;
  const names = rowNames();
  body.appendChild(buttonRow('New layer', () => newLayer()));
  let shown = 0;
  // Top layer first. A layer with nothing on it is listed only if the user made
  // it (or it is the current one) — the empty default layers stay out of the way.
  for (const l of [...m.layers].reverse()) {
    if (l.home === 'imported') {
      const probe = document.createElement('div');
      const count = importedRows(probe);
      if (!count) continue;
      layerHeader(body, l, count);
      if (!layersCollapsed.has(l.id)) { probe.className = 'parts-tree'; body.appendChild(probe); }
      shown++;
      continue;
    }
    const units = (m.units.get(l.id) || []).filter((u) => u.kind !== 'table' || state.showSchedule);
    const count = units.reduce((n, u) => n + unitObjectIds(u).filter((id) => !id.startsWith('table:') || state.showSchedule).length, 0);
    if (!count && l.home && l.id !== partsTab.currentLayerId) continue;
    layerHeader(body, l, count);
    shown++;
    if (layersCollapsed.has(l.id)) continue;
    const tree = document.createElement('div');
    tree.className = 'parts-tree' + (l.hidden ? ' layer-rows-hidden' : '');
    body.appendChild(tree);
    for (const u of [...units].reverse()) layerUnitRow(tree, u, l.id, names);
  }
  if (!shown) {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = tr('No products placed yet. Drag one from the left.');
    body.appendChild(e);
  }
}

/** Select an image/PDF underlay from the Parts list: enter its transform mode so
 *  it can be moved, resized, rotated and deleted like any other object. */
function selectBackgroundFromList(id: string | null): void {
  setTool('select'); // setTool clears editPdf, so enter transform mode afterwards
  clearSelection(state);
  enterEdit({ kind: 'underlay', id });
  renderProperties();
  invalidate();
}

/** Select the imported DXF background so it can be dragged / deleted as an object. */
export function selectBackgroundGeom(): void {
  setTool('select'); // setTool clears the edit modes, so enter this one afterwards
  clearSelection(state);
  enterEdit({ kind: 'plan' });
  renderProperties();
  invalidate();
}
